import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  styled,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone } from "react-dropzone";
import { UploadFile } from "@mui/icons-material";
import Avatar from "@mui/material/Avatar";
import { Link, paletteVar, Typography } from "@platform/design-system";

import { formatBytes, validateFileName, validateFileSize } from "../utils";
import { submitEvent } from "../utils/tracking";

import UploadFileError from "./uploadFileError";

const DropZoneBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(9, 0),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: theme.spacing(13),
  fontFamily: theme.typography.fontFamily,
  overflow: "hidden",
  position: "relative",
  border: `1px dashed ${theme.palette.dropzone.lineSoft}`,
}));

const DropZoneFilesBox = styled(Box)(({ theme }) => ({
  maxHeight: theme.spacing(44),
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const FileBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
}));

const FileInfo = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  display: "flex",
  flex: "1",
  flexDirection: "column",
}));

export const RenderFile = ({
  file,
  icon,
  onFileDownload = () => {},
  avatarIcon,
  ...rest
}) => {
  const theme = useTheme();
  return (
    <FileBox
      {...rest}
      elevation={1}
      sx={{
        marginY: theme.spacing(0.5),
      }}
    >
      <Avatar sx={{ bgcolor: paletteVar("surfaces-secondary", "#FAF0FA") }}>
        {avatarIcon ?? <></>}
      </Avatar>
      <FileInfo onClick={() => !file.loading && onFileDownload(file)}>
        <Typography variant="body-large" sx={{ wordBreak: "break-all" }}>
          {file.displayName || file.name}
        </Typography>
        <Typography variant="body-medium" color="textSecondary">
          <strong>
            {!file.loading && `${formatBytes(file.size || file.contentLength)}`}
          </strong>
        </Typography>
        {file.loading && <LinearProgress sx={{ marginTop: 1 }} />}
      </FileInfo>
      {!file.loading && (icon ?? <></>)}
    </FileBox>
  );
};

const notifyRejection = (
  fileRejected,
  filesAlreadyNotified,
  setFilesAlreadyNotified,
) => {
  if (
    !filesAlreadyNotified.find(
      (currFile) => currFile === fileRejected.file.name,
    )
  ) {
    submitEvent("RFP Upload Error", {
      error: fileRejected.errors[0].code,
      fileName: fileRejected.file.name,
    });
    setFilesAlreadyNotified([...filesAlreadyNotified, fileRejected.file.name]);
  }
};

const RenderFileRejected = ({
  fileRejected,
  maxSize,
  allowedFormatLabel,
  onRemove,
}) => {
  const { errors, file } = fileRejected;
  const error = errors[0];
  const [filesAlreadyNotified, setFilesAlreadyNotified] = useState([]);
  const [errorViewedAlreadyNotified, setErrorViewedAlreadyNotified] = useState(
    [],
  );

  const typesUploadErrors = {
    "file-invalid-type": {
      message: ` File extension is not supported (allowed ${allowedFormatLabel})`,
    },
    duplicate: {
      message:
        "You have uploaded a duplicate file. Please make sure you are uploading the correct file or rename the file and try again.",
    },
    oversize: {
      message: `The total size of all uploaded documents cannot exceed (${maxSize}MB).`,
    },
    "too-many-files": {
      message: (
        <>
          {" "}
          <Typography component="span" color="inherit">
            <strong>Uploading multiple files is not supported.</strong>
          </Typography>{" "}
          Please upload, assign, and save one file at a time.
        </>
      ),
    },
    "invalid-characters": {
      message: `File names cannot contain special characters ("*/?<>:|).`,
    },
  };

  const errorReason = typesUploadErrors[error.code]?.message;

  const errorKey = `${file.name}_${error.code}`;
  useEffect(() => {
    notifyRejection(
      fileRejected,
      filesAlreadyNotified,
      setFilesAlreadyNotified,
    );
  }, [fileRejected, filesAlreadyNotified]);
  useEffect(() => {
    if (!errorViewedAlreadyNotified.includes(errorKey)) {
      submitEvent("Error Viewed", {
        error_type: "file_rejected",
        error_reason: errorReason,
      });
      setErrorViewedAlreadyNotified([...errorViewedAlreadyNotified, errorKey]);
    }
  }, [errorViewedAlreadyNotified, errorKey, errorReason]);

  return (
    <UploadFileError
      fileName={file.name}
      error={errorReason}
      onClose={() => onRemove(file)}
      code={error.code}
    />
  );
};

const CustomDropzone = ({
  files,
  handleUploadFiles,
  maxSize,
  allowDuplicated = false,
  onFileDownload = () => {},
  onFileDelete = () => {},
  handleValidation = () => {},
  acceptedFormatFiles,
  allowedFormatLabel,
  isMultiple,
  isAllowToManage,
  isLoading = false,
  renderFiles = true,
}) => {
  const theme = useTheme();
  const [rejectedFiles, setRejectedFiles] = useState([]);

  // Read through a ref so the upload effect below doesn't depend on the
  // handler's identity: callers pass inline functions, and re-running the
  // effect on every render would re-upload the same accepted files.
  const handleUploadFilesRef = useRef(handleUploadFiles);
  useEffect(() => {
    handleUploadFilesRef.current = handleUploadFiles;
  });

  const handleDefaultValidation = ({ name, size }) => {
    handleValidation({ name, size });
    try {
      validateFileSize(size, maxSize);
    } catch {
      return {
        code: "oversize",
      };
    }

    if (!allowDuplicated) {
      var filterDocs = files?.some(
        (file) => file.name === name || file.displayName === name,
      );

      if (filterDocs) {
        return {
          code: "duplicate",
        };
      }
    }

    try {
      validateFileName(name);
    } catch {
      return {
        code: "invalid-characters",
      };
    }
  };

  const { acceptedFiles, fileRejections, getRootProps, getInputProps } =
    useDropzone({
      accept: acceptedFormatFiles,
      validator: handleDefaultValidation,
      multiple: isMultiple,
    });

  useEffect(() => {
    const rejections = [...fileRejections];
    if (acceptedFiles.length > 0) {
      try {
        validateFileSize(
          acceptedFiles.reduce((acc, file) => acc + file.size, 0),
          maxSize,
        );
        handleUploadFilesRef.current([...acceptedFiles]);
      } catch {
        rejections.push({
          file: {
            name: acceptedFiles.map((file) => file.name).join(", "),
          },
          errors: [
            {
              code: "oversize",
            },
          ],
        });
      }
    }

    const tooManyRejection = rejections.find(
      (r) => r.errors[0].code === "too-many-files",
    );
    if (tooManyRejection) {
      setRejectedFiles([tooManyRejection]);
    } else {
      setRejectedFiles(rejections);
    }
  }, [acceptedFiles, fileRejections, maxSize]);

  const handleDelete = (file) => {
    onFileDelete(file);
  };

  const handleRemoveRejected = (file) => {
    const newFiles = rejectedFiles.filter(
      (currFile) => currFile.file.name !== file.name,
    );
    setRejectedFiles(newFiles);
  };
  return (
    <>
      {rejectedFiles.length ? (
        <DropZoneFilesBox
          mt={2}
          sx={{
            maxHeight: theme.spacing(44),
            overflow: "auto",
          }}
        >
          {rejectedFiles.map((fileRejected) => (
            <RenderFileRejected
              key={`${fileRejected.file?.name || "unknown"}:${fileRejected.errors?.[0]?.code || "err"}`}
              maxSize={maxSize}
              fileRejected={fileRejected}
              allowedFormatLabel={allowedFormatLabel}
              onRemove={handleRemoveRejected}
            />
          ))}
        </DropZoneFilesBox>
      ) : null}
      {isLoading ? (
        <Grid textAlign="center" mt={2}>
          <CircularProgress size={20} />
        </Grid>
      ) : (
        isAllowToManage && (
          <DropZoneBox {...getRootProps({ mt: theme.spacing(3) })}>
            <input {...getInputProps()} />
            <Typography variant="body-large">
              <strong>
                <Link component="span">Click to upload</Link> or drag and drop
              </strong>
            </Typography>
            <Typography variant="body-medium" mt={1}>
              <strong>{allowedFormatLabel}</strong>
            </Typography>
          </DropZoneBox>
        )
      )}
      {renderFiles && files?.length > 0 && (
        <Box
          mt={2}
          sx={{
            maxHeight: theme.spacing(44),
            overflow: "auto",
          }}
        >
          {files?.map((file) => (
            <RenderFile
              avatarIcon={<UploadFile color={"brandPrimary"} />}
              key={file.id || file.name}
              file={file}
              icon={
                isAllowToManage &&
                !isLoading && (
                  <IconButton onClick={() => handleDelete(file)}>
                    <CloseIcon />
                  </IconButton>
                )
              }
              onFileDownload={onFileDownload}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default CustomDropzone;
